import clsx from "clsx";
import type { FocusEvent, ReactNode } from "react";

export type PanelTone = "brand" | "alt";

// The signature Boba Bash container: a little desktop window. A tinted title
// bar with a filename-ish label, an optional red close affordance, and a cream
// body underneath. Used for cards, sections and modals alike.
export default function Panel({
  title,
  tone = "brand",
  onClose,
  actions,
  children,
  className,
  bodyClassName,
  // Lets a caller label a rendered panel so it can be found in the DOM later -
  // the page editor scrolls to one to show where a section landed.
  "data-slot": dataSlot,
  onFocusCapture,
  onBlurCapture,
}: {
  title?: string;
  tone?: PanelTone;
  onClose?: () => void;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  "data-slot"?: string;
  onFocusCapture?: (e: FocusEvent<HTMLDivElement>) => void;
  onBlurCapture?: (e: FocusEvent<HTMLDivElement>) => void;
}) {
  return (
    <div
      data-slot={dataSlot}
      onFocusCapture={onFocusCapture}
      onBlurCapture={onBlurCapture}
      className={clsx(
        "border-line bg-surface shadow-card flex flex-col border-2",
        className,
      )}
    >
      {(title || onClose || actions) && (
        <div
          className={clsx(
            "border-line flex items-center gap-3 border-b-2 px-3 py-1.5",
            tone === "brand" ? "bg-titlebar" : "bg-titlebar-alt",
          )}
        >
          <span className="font-display text-caption text-titlebar-ink min-w-0 flex-1 truncate font-bold">
            {title}
          </span>

          {actions}

          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="border-line bg-close font-display text-caption flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center border leading-none font-bold text-white transition-transform hover:scale-110"
            >
              ×
            </button>
          )}
        </div>
      )}

      <div className={clsx("flex-1 p-5", bodyClassName)}>{children}</div>
    </div>
  );
}
